import React, { useEffect, useRef, useCallback } from 'react';
import '../styles/CanvaStyleFireworks.css';

const CanvaStyleFireworks = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const rocketsRef = useRef([]);
  const particlesRef = useRef([]);
  const streaksRef = useRef([]);
  const flashRef = useRef({ a: 0, x: 0, y: 0, r: 0 });
  const shakePowerRef = useRef(0);
  const autoRef = useRef(true);

  // Constants
  const TAU = Math.PI * 2;
  const gravity = 0.12;
  const air = 0.999;
  
  // Gold palette (soft variations for realism)
  const goldPalette = ['#fff7d6', '#ffe8a3', '#ffd66b', '#f9c846', '#e8b547'];

  // Utility functions
  const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
  const rand = (a, b) => a + Math.random() * (b - a);
  const chance = p => Math.random() < p;

  const rgba = (hex, a = 1) => {
    const h = hex.replace('#', '');
    const n = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    const num = parseInt(n, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r},${g},${b},${a})`;
  };

  const shuffle = (arr) => {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  const pick = (vals, weights) => {
    const sum = weights.reduce((s, w) => s + w, 0);
    let r = Math.random() * sum;
    for (let i = 0; i < vals.length; i++) {
      if ((r -= weights[i]) <= 0) return vals[i];
    }
    return vals[0];
  };

  // Streak class
  class Streak {
    constructor(x1, y1, x2, y2, color, life = 560, width = 1.5) {
      this.x1 = x1;
      this.y1 = y1;
      this.x2 = x2;
      this.y2 = y2;
      this.life = life;
      this.max = life;
      this.color = color;
      this.width = width;
    }

    step(dt) {
      this.life -= dt * 1000;
      return this.life > 0;
    }

    draw(c) {
      const a = Math.max(0, Math.min(1, this.life / this.max));
      c.globalCompositeOperation = 'lighter';
      const g = c.createLinearGradient(this.x1, this.y1, this.x2, this.y2);
      const alpha = 0.48 * a;
      g.addColorStop(0, this.color.replace('ALPHA', '0'));
      g.addColorStop(0.35, this.color.replace('ALPHA', alpha.toFixed(3)));
      g.addColorStop(1, this.color.replace('ALPHA', '0'));
      c.strokeStyle = g;
      c.lineWidth = this.width * (0.8 + 0.2 * a);
      c.beginPath();
      c.moveTo(this.x1, this.y1);
      c.lineTo(this.x2, this.y2);
      c.stroke();
    }
  }

  // Rocket class
  class Rocket {
    constructor(x, y, targetY, palette) {
      this.x = x;
      this.y = y;
      this.vx = rand(-0.32, 0.32);
      this.vy = rand(-6.2, -7.2);
      this.palette = palette;
      this.core = palette[0];
      this.spark = palette[Math.floor(1 + Math.random() * (palette.length - 1))];
      this.targetY = targetY;
      this.lastX = x;
      this.lastY = y;
      this.exploded = false;
    }

    step(dt) {
      this.lastX = this.x;
      this.lastY = this.y;
      this.vy += gravity * dt * 36;
      this.vx *= 0.999;
      this.x += this.vx * dt * 60;
      this.y += this.vy * dt * 60;

      // Slender ascent streak
      if (chance(0.55)) {
        streaksRef.current.push(new Streak(
          this.lastX, this.lastY, this.x, this.y,
          rgba(this.spark, 0.45), 460, 1.3
        ));
      }

      if (this.vy >= -0.52 || this.y <= this.targetY) {
        this.explode();
        return false;
      }
      return this.y > -40 && this.x > -40 && this.x < canvasRef.current.width + 40;
    }

    explode() {
      if (this.exploded) return;
      this.exploded = true;
      
      const baseCount = 90 + Math.floor(rand(0, 50));
      const count = Math.floor(baseCount * 0.85); // Calmest
      const type = pick(['sphere', 'peony', 'chrys', 'ring', 'willow'], [0.5, 0.28, 0.1, 0.06, 0.06]);
      const pal = shuffle([...this.palette]);
      
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * TAU;
        let speed = rand(2.0, 3.6) * 0.95; // Slowest spread
        let vx = Math.cos(angle) * speed;
        let vy = Math.sin(angle) * speed;
        
        if (type === 'ring') {
          const t = (i / count) * TAU;
          vx = Math.cos(t) * speed * 1.08;
          vy = Math.sin(t) * speed * 1.08;
        }
        if (type === 'chrys') {
          vx *= 1.04;
          vy *= 1.04;
        }
        if (type === 'willow') {
          vy += rand(0.6, 0.9);
        }
        
        const col = pal[i % pal.length];
        particlesRef.current.push(new Particle(
          this.x, this.y, vx, vy, col, type, chance(0.25)
        ));
      }
      
      flashAt(this.x, this.y, 0.14); // Gentlest bloom
      shake(2); // Very light camera shake
    }

    draw(c) {
      c.globalCompositeOperation = 'lighter';
      const r = 1.7;
      const g = c.createRadialGradient(this.x, this.y, 0, this.x, this.y, r * 6);
      g.addColorStop(0, rgba(this.core, 0.85));
      g.addColorStop(1, rgba(this.core, 0.0));
      c.fillStyle = g;
      c.beginPath();
      c.arc(this.x, this.y, r * 1.7, 0, TAU);
      c.fill();
    }
  }

  // Particle class
  class Particle {
    constructor(x, y, vx, vy, color, type, sparkle) {
      this.x = x;
      this.y = y;
      this.vx = vx;
      this.vy = vy;
      this.color = color;
      this.type = type;
      this.sparkle = sparkle;
      this.life = rand(1.7, 2.7); // Longer fade
      this.age = 0;
      this.size = rand(1.0, 1.6);
      this.lastX = x;
      this.lastY = y;
    }

    step(dt) {
      this.age += dt;
      this.lastX = this.x;
      this.lastY = this.y;
      this.vx *= air;
      this.vy *= air;
      this.vy += gravity * dt * 0.82;
      
      if (this.type === 'willow') {
        this.vx *= 0.997;
        this.vy += 0.01;
      }
      
      this.x += this.vx * dt * 60;
      this.y += this.vy * dt * 60;

      if (chance(0.22)) {
        streaksRef.current.push(new Streak(
          this.lastX, this.lastY, this.x, this.y,
          rgba(this.color, 0.3), 560, 1.1
        ));
      }
      
      return this.age < this.life && this.x > -20 && this.x < canvasRef.current.width + 20 && this.y < canvasRef.current.height + 20;
    }

    draw(c) {
      const p = 1 - (this.age / this.life);
      const a = Math.max(0, Math.min(0.7, 0.12 + p * 0.6));
      c.globalCompositeOperation = 'lighter';

      // Warm golden halo
      c.fillStyle = rgba(this.color, 0.05 + 0.16 * a);
      c.beginPath();
      c.arc(this.x, this.y, this.size * 4.0, 0, TAU);
      c.fill();

      // Core dot
      const sparkle = this.sparkle ? (1 + Math.sin(this.age * 50) * 0.12) : 1;
      c.fillStyle = rgba(this.color, 0.78);
      c.beginPath();
      c.arc(this.x, this.y, Math.max(0.8, this.size * 0.95 * sparkle), 0, TAU);
      c.fill();
    }
  }

  // Flash and shake functions
  const flashAt = (x, y, power) => {
    flashRef.current.a = Math.min(0.24, flashRef.current.a + power);
    flashRef.current.x = x;
    flashRef.current.y = y;
    flashRef.current.r = 420;
  };

  const shake = (p) => {
    shakePowerRef.current = Math.max(shakePowerRef.current, p);
  };

  const applyShake = (ctx, dpr) => {
    const dx = (Math.random() - 0.5) * shakePowerRef.current;
    const dy = (Math.random() - 0.5) * shakePowerRef.current;
    shakePowerRef.current *= 0.94;
    ctx.setTransform(dpr, 0, 0, dpr, dx, dy);
  };

  // Launch function
  const launch = useCallback((x = rand(canvasRef.current.width * 0.1, canvasRef.current.width * 0.4)) => {
    const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    const W = canvasRef.current.width / dpr;
    const H = canvasRef.current.height / dpr;
    
    // Use logical width for positioning - spread more toward middle
    const logicalX = rand(W * 0.2, W * 0.6);
    
    // Vary target heights - some go to corners, some stay in middle
    let targetY;
    const cornerChance = Math.random();
    if (cornerChance < 0.3) {
      // 30% chance for top-left corner
      targetY = rand(H * 0.02, H * 0.08);
    } else if (cornerChance < 0.6) {
      // 30% chance for top-right corner  
      targetY = rand(H * 0.02, H * 0.08);
    } else {
      // 40% chance for middle area
      targetY = rand(H * 0.05, H * 0.2);
    }
    
    const palette = goldPalette; // Always gold
    const rocket = new Rocket(logicalX, H + 10, targetY, palette);
    rocketsRef.current.push(rocket);
  }, []);

  // Animation loop
  const animate = useCallback((now) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: false, desynchronized: true });
    const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    const W = canvas.width / dpr;
    const H = canvas.height / dpr;

    const dt = Math.min(0.033, (now - (animate.lastTime || now)) / 1000);
    animate.lastTime = now;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    applyShake(ctx, dpr);

    // Slight persistence for afterglow
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = 'rgba(6, 9, 24, 0.18)';
    ctx.fillRect(-50, -50, W + 100, H + 100);

    // Update rockets
    for (let i = rocketsRef.current.length - 1; i >= 0; i--) {
      const r = rocketsRef.current[i];
      if (!r.step(dt)) {
        rocketsRef.current.splice(i, 1);
      } else {
        r.draw(ctx);
      }
    }

    // Update particles
    for (let i = particlesRef.current.length - 1; i >= 0; i--) {
      const p = particlesRef.current[i];
      if (!p.step(dt)) {
        particlesRef.current.splice(i, 1);
      } else {
        p.draw(ctx);
      }
    }

    // Update streaks
    for (let i = streaksRef.current.length - 1; i >= 0; i--) {
      const s = streaksRef.current[i];
      if (!s.step(dt)) {
        streaksRef.current.splice(i, 1);
        continue;
      }
      s.draw(ctx);
    }

    // Flash effect
    if (flashRef.current.a > 0.002) {
      const grad = ctx.createRadialGradient(
        flashRef.current.x, flashRef.current.y, 0,
        flashRef.current.x, flashRef.current.y, flashRef.current.r
      );
      grad.addColorStop(0, `rgba(255,240,200,${flashRef.current.a})`);
      grad.addColorStop(1, 'rgba(255,240,200,0)');
      ctx.globalCompositeOperation = 'lighter';
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);
      flashRef.current.a *= 0.94;
    }

    // Auto cadence: more frequent fireworks
    if (autoRef.current) {
      const frequent = 0.012; // Increased intensity cadence
      if (Math.random() < frequent) {
        launch();
        if (Math.random() < 0.25) {
          // Launch additional fireworks with varied positioning
          const cornerLaunch = Math.random();
          if (cornerLaunch < 0.4) {
            // 40% chance for left side (top-left corner area)
            launch(rand(W * 0.1, W * 0.3));
          } else if (cornerLaunch < 0.8) {
            // 40% chance for right side (top-right corner area)
            launch(rand(W * 0.7, W * 0.9));
          } else {
            // 20% chance for middle
            launch(rand(W * 0.2, W * 0.8));
          }
        }
      }
    }

    animationRef.current = requestAnimationFrame(animate);
  }, [launch]);

  // Setup canvas
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    const W = Math.floor(window.innerWidth);
    const H = Math.floor(window.innerHeight);
    
    canvas.width = Math.floor(W * dpr);
    canvas.height = Math.floor(H * dpr);
    canvas.style.width = W + 'px';
    canvas.style.height = H + 'px';
    
    const ctx = canvas.getContext('2d', { alpha: false, desynchronized: true });
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }, []);

  // Initialize
  useEffect(() => {
    setupCanvas();
    
    // Gentle start
    setTimeout(() => {
      for (let i = 0; i < 2; i++) {
        launch(rand(canvasRef.current.width * 0.3, canvasRef.current.width * 0.7));
      }
    }, 500);

    // Start animation
    animationRef.current = requestAnimationFrame(animate);

    // Handle resize
    const handleResize = () => {
      setupCanvas();
    };

    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [setupCanvas, animate, launch]);

  return (
    <div className="canva-fireworks-container">
      <canvas
        ref={canvasRef}
        className="canva-fireworks-canvas"
        aria-label="Subtle gold fireworks animation"
        role="img"
      />
    </div>
  );
};

export default CanvaStyleFireworks;